/**
 * IsgTaahhutnameIndir.jsx — "Formu indir: Türkçe | English" for the Firma İSG Taahhütnamesi (file type 79), backlog #541.
 *
 * Replaces the uploaded blank form (document_template, #503) on that one document: the PDF is made here, for THIS company at THIS fair, with
 * the fair logo, the company, the hall / booth and the phase dates filled in (services/isgTaahhutnameService.js + isgTaahhutnamePdf.js).
 * Two modes. Staff (Firma Profil Inceleme): the two buttons are the two languages of the form, whatever the UI language is. What was read from
 * the backend is kept while the company and the fair stay the same, so the second language is instant. Company (Firma Karti, #668): `tekDil`
 * = ONE button, "Download blank form", that makes the form in the language the user has selected (WAS #541: both languages on the company tile).
 */
import React, { useRef, useState } from "react";
import { Button, Spinner } from "reactstrap";
import { Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { isgTaahhutnameVerisiOku, isgTaahhutnameIndir, ISG_TAAHHUTNAME_DILLERI } from "../../services/isgTaahhutnameService";

/**
 * @param firmaId   the company; with `fuarId` it picks the stands, the dates and the logo
 * @param firmaAdi  printed in the Exhibitor Company cell
 * @param fuarId    the selected fair
 * @param hizalama  "center" (a card tile) or "start" (a table cell)
 * @param tekDil    one button, in the UI language (Turkish or English), instead of one per language
 * @param etiket    the one button's text (with `tekDil`)
 */
export default function IsgTaahhutnameIndir({ firmaId, firmaAdi, fuarId, hizalama = "center", className = "mt-2 small", tekDil = false, etiket: tekEtiket }) {
  const { t, i18n } = useTranslation("pdf");
  const [yukleniyor, setYukleniyor] = useState(null);
  const [hata, setHata] = useState(null);
  const [logoSorunu, setLogoSorunu] = useState(false);
  const onbellek = useRef({ anahtar: null, sonuc: null });
  const hazir = Boolean(firmaId && fuarId);

  async function indir(dil) {
    setYukleniyor(dil);
    setHata(null);
    try {
      const anahtar = `${firmaId}/${fuarId}/${firmaAdi || ""}`;
      if (onbellek.current.anahtar !== anahtar) onbellek.current = { anahtar, sonuc: await isgTaahhutnameVerisiOku({ firmaId, firmaAdi, fuarId }) };
      const { veri, logoSorunu: sorun } = onbellek.current.sonuc;
      setLogoSorunu(sorun);
      await isgTaahhutnameIndir(veri, dil);
    } catch (e) {
      onbellek.current = { anahtar: null, sonuc: null };
      setHata(e?.message || String(e));
    } finally {
      setYukleniyor(null);
    }
  }

  const arayuzDili = i18n?.language === "en" ? "en" : "tr";
  const diller = tekDil ? [arayuzDili] : ISG_TAAHHUTNAME_DILLERI;
  const etiket = tekDil ? { [arayuzDili]: tekEtiket || t("isgTaahhutname.ui.formuIndir") } : { tr: t("isgTaahhutname.ui.dilTr"), en: t("isgTaahhutname.ui.dilEn") };
  return (
    <div className={className} data-testid="isg-taahhutname-indir">
      {!tekDil && <div className="text-muted">{t("isgTaahhutname.ui.formuIndir")}</div>}
      <div className={`d-flex gap-3 ${hizalama === "center" ? "justify-content-center" : "justify-content-start"}`}>
        {diller.map((dil) => (
          <Button
            key={dil} color="link" size="sm" className="p-0" disabled={!hazir || Boolean(yukleniyor)}
            onClick={() => indir(dil)} data-testid={`isg-taahhutname-${dil}`}
          >
            {yukleniyor === dil ? <Spinner size="sm" className="me-1" /> : <Download size={12} className="me-1" />}
            {yukleniyor === dil ? t("isgTaahhutname.ui.hazirlaniyor") : etiket[dil]}
          </Button>
        ))}
      </div>
      {hata && <div className="text-danger" data-testid="isg-taahhutname-hata">{t("isgTaahhutname.ui.hata", { hata })}</div>}
      {!hata && logoSorunu && <div className="text-warning" data-testid="isg-taahhutname-logo-sorunu">{t("isgTaahhutname.ui.logoOkunamadi")}</div>}
    </div>
  );
}
